export interface LazyScheduleOptions {
	/** Delay after `load` before requesting idle time. Default 1500ms. */
	delayMs?: number;
	/** Max wait for `requestIdleCallback` before forcing the task. Default 5000ms. */
	idleTimeoutMs?: number;
}

type IdleWindow = Window & {
	requestIdleCallback?: (
		callback: IdleRequestCallback,
		options?: IdleRequestOptions
	) => number;
};

/**
 * Defer a one-off task until after first paint + browser idle.
 *
 * Shared scheduling for lazy third-party scripts (Clarity, Dotdigital):
 * waits for `load` (or runs now if already loaded), then
 * `delayMs`, then `requestIdleCallback` (with a hard `idleTimeoutMs`
 * ceiling). Keeps non-critical vendor scripts off the critical path.
 *
 * Client-only — call from a `.client.ts` plugin. The `task` itself
 * should be idempotent (e.g. guard on an existing script id).
 */
export const scheduleLazyScript = (
	task: () => void,
	options: LazyScheduleOptions = {}
): void => {
	const { delayMs = 1500, idleTimeoutMs = 5000 } = options;

	const runWhenIdle = () => {
		window.setTimeout(() => {
			const idleWindow = window as IdleWindow;
			if (typeof idleWindow.requestIdleCallback === "function") {
				idleWindow.requestIdleCallback(task, { timeout: idleTimeoutMs });
				return;
			}
			task();
		}, delayMs);
	};

	if (document.readyState === "complete") {
		runWhenIdle();
		return;
	}
	window.addEventListener("load", runWhenIdle, { once: true });
};
